import React, { useEffect, useState } from 'react';
import { FaArrowUp, FaArrowDown, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import Field, { TextInput, TextArea, NativeSelect, controlClass } from '../../../components/ui/Field';
import ExprInput, { KVList } from './ExprInput';
import Combo from '../../../components/ui/Combo';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import { engineService } from '../../../services/engineService';
import { CONTEXT_KEYS, FILTER_OPS, INPUT_SOURCES, INPUT_TYPES, NODE_TYPES, SUPABASE_OPS, SHEETS_OPS, COND_OPS, NODE_CONNECTOR, CONNECTOR_KINDS, usesConnector, httpAuthType, HTTP_AUTHS, HTTP_BODY_MODES, VAR_TYPES, locate, getList, updateNode, variablesFor, previousNodes, pathsFromSample } from '../../../utils/toolDefinition';

// Panel derecho del editor: lo que se configura del paso elegido en el lienzo. El primer nodo («Cuando el agente la usa») tiene lo de
// la herramienta completa: para qué sirve, cómo la usa el agente, sus datos de entrada y sus conectores.

const schemaCache = new Map();
function useSchema(connectorId, reload = 0) {
  const [s, setS] = useState(() => (connectorId ? schemaCache.get(connectorId) : null) || { state: connectorId ? 'loading' : 'none' });
  useEffect(() => {
    if (!connectorId) { setS({ state: 'none' }); return undefined; }
    if (schemaCache.get(connectorId)?.state === 'ready') { setS(schemaCache.get(connectorId)); return undefined; }
    let live = true; setS({ state: 'loading' });
    engineService.connectorSchema(connectorId)
      .then((d) => { const v = { state: 'ready', ...d }; schemaCache.set(connectorId, v); if (live) setS(v); })
      .catch((e) => { if (live) setS({ state: 'error', message: e.message }); });
    return () => { live = false; };
  }, [connectorId, reload]);
  return s;
}

const Section = ({ title, hint, children, action }) => (
  <section className="pt-5 first:pt-0">
    <div className="flex items-start gap-3 mb-3">
      <div className="flex-1"><h3 className="text-[13.5px] font-semibold text-fg">{title}</h3>{hint && <p className="mt-0.5 text-[12.5px] text-fg-dim">{hint}</p>}</div>
      {action}
    </div>
    {children}
  </section>
);

export default function Inspector({ draft, tool, selected, onChangeDraft, onChangeNode, onMove, onRemove, connectors, sample, onSample, warnings, onNewConnector, results }) {
  if (selected === '__entrada') return <ToolSettings draft={draft} tool={tool} onChange={onChangeDraft} connectors={connectors} sample={sample} onSample={onSample} warnings={warnings?._tool} />;
  const at = locate(draft.nodes || [], selected);
  const node = at?.node;
  if (!node) return <p className="text-[13.5px] text-fg-dim">Elige un paso en el lienzo para configurarlo.</p>;
  const idx = at.index;
  const siblings = getList(draft.nodes || [], at.addr);
  const kind = usesConnector(node) ? NODE_CONNECTOR[node.type] : null;
  const slotConn = (slot) => { const s = (draft.connectors || []).find((x) => x.slot === slot); return s ? (s.mode === 'fixed' ? s.connector_id : sample[slot]) : null; };
  // Datos para el selector { }: entradas y pasos anteriores, más las columnas reales de sus tablas y lo que devolvieron en la última prueba.
  const vars = [...variablesFor(draft, node.id)];
  for (const p of previousNodes(draft.nodes, node.id)) {
    const out = results?.[p.id]?.output;
    if (out !== undefined) vars.push(...pathsFromSample(`nodos.${p.id}`, out, p.name || p.id).map((v) => ({ ...v, group: `Última prueba · ${p.name || p.id}` })));
    else if (p.type === 'supabase' && p.table) {
      const sch = schemaCache.get(slotConn(p.slot));
      const t = sch?.tables?.find((x) => x.name === p.table);
      if (t) vars.push(...t.columns.map((c) => ({ path: `nodos.${p.id}.data.0.${c.name}`, label: `${p.name} · ${c.name}`, hint: c.type, group: `Columnas de ${p.table} (primera fila)` })));
    }
  }
  const set = (patch) => onChangeNode(node.id, patch);
  const picker = kind ? <ConnectorPicker kind={kind} node={node} draft={draft} tool={tool} connectors={connectors} onChangeDraft={onChangeDraft} onNewConnector={onNewConnector} sample={sample} onSample={onSample} /> : null;
  return (
    <div className="divide-y divide-line [&>*]:pb-5">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: NODE_TYPES[node.type].accent }} aria-hidden />
        <p className="text-[12px] font-semibold text-fg-faint">{NODE_TYPES[node.type].label}</p>
        <div className="ml-auto flex gap-1">
          <IconBtn label="Subir el paso" onClick={() => onMove(node.id, -1)} disabled={idx === 0}><FaArrowUp /></IconBtn>
          <IconBtn label="Bajar el paso" onClick={() => onMove(node.id, 1)} disabled={idx === siblings.length - 1}><FaArrowDown /></IconBtn>
          <IconBtn label="Quitar el paso" onClick={() => onRemove(node.id)}><FaTrash /></IconBtn>
        </div>
      </div>
      <Section title="Nombre del paso">
        <TextInput value={node.name || ''} onChange={(e) => set({ name: e.target.value })} maxLength={80} aria-label="Nombre del paso" />
        <p className="mt-1.5 text-[12px] text-fg-faint">Sus resultados se usan como <code className="font-mono">{`{{nodos.${node.id}…}}`}</code></p>
      </Section>
      {warnings?.[node.id]?.length > 0 && (
        <ul className="pt-4 text-[12.5px] text-caution list-disc pl-4">{warnings[node.id].map((w) => <li key={w}>{w}</li>)}</ul>
      )}
      {at.addr.length > 0 && <p className="pt-3 text-[12px] text-fg-faint">Dentro del camino «{at.addr[at.addr.length - 1] === 'then' ? 'Sí' : 'No'}» de un «Si / no».</p>}
      {node.type === 'if' && <p className="pt-3 text-[12px] text-fg-faint">Quitar este paso quita también todo lo que hay en sus dos caminos.</p>}
      {kind && node.type !== 'http' && picker}
      {node.type === 'supabase' && <SupabaseEditor node={node} set={set} vars={vars} connectorId={slotConn(node.slot)} />}
      {node.type === 'http' && <HttpEditor node={node} set={set} vars={vars} picker={picker} />}
      {node.type === 'code' && <CodeEditor node={node} set={set} vars={vars} />}
      {node.type === 'sheets' && <SheetsEditor node={node} set={set} vars={vars} connectorId={slotConn(node.slot)} />}
      {node.type === 'if' && <IfEditor node={node} set={set} vars={vars} />}
      {node.type === 'set' && (
        <Section title="Campos" hint="Cada campo queda como {{nodos.<paso>.<campo>}} para los pasos siguientes.">
          <KVList items={node.fields} onChange={(fields) => set({ fields })} vars={vars} keyLabel="campo" addLabel="Agregar campo" idPrefix={node.id} />
        </Section>
      )}
      {node.type === 'respond' && (
        <Section title="Lo que recibe el agente" hint="El resultado que el agente usa para contestarle a la persona. Arma el texto con datos de los pasos (botón { }) y métodos, por ejemplo {{nodos.buscar.data | cantidad}}.">
          <ExprInput multiline value={node.text} onChange={(text) => set({ text })} vars={vars} ariaLabel="Respuesta al agente" placeholder="Encontré {{nodos.buscar.data | cantidad}} citas; la próxima es el {{nodos.buscar.data.0.fecha | fecha:&quot;larga&quot;}}." />
        </Section>
      )}
      <RemoveStep node={node} onRemove={onRemove} />
    </div>
  );
}

/** «Quitar este paso», a la vista y con texto. Un «Si / no» con pasos adentro pide confirmar porque se llevan sus dos caminos. */
function RemoveStep({ node, onRemove }) {
  const [asking, setAsking] = useState(false);
  const inside = node.type === 'if' ? (node.then || []).length + (node.else || []).length : 0;
  return (
    <div className="pt-5">
      <Btn variant="ghost" icon={FaTrash} onClick={() => (inside ? setAsking(true) : onRemove(node.id))}>Quitar este paso</Btn>
      <ConfirmModal open={asking} onClose={() => setAsking(false)} tone="danger" title="Quitar el «Si / no»" confirmLabel="Quitar el paso y sus caminos" onConfirm={async () => onRemove(node.id)}>
        <p>Tiene {inside === 1 ? '1 paso' : `${inside} pasos`} adentro de sus caminos, y se quitan junto con él.</p>
      </ConfirmModal>
    </div>
  );
}

function IconBtn({ label, children, ...rest }) {
  return <button type="button" aria-label={label} title={label} className="h-8 w-8 grid place-items-center rounded-lg text-fg-dim hover:text-fg hover:bg-raised disabled:opacity-30 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 [&>svg]:h-3 [&>svg]:w-3" {...rest}>{children}</button>;
}

/**
 * El conector del paso, elegido aquí mismo: uno que la herramienta ya usa, uno de Integraciones (se agrega solo a la herramienta),
 * «que lo elija cada agente» (herramientas globales) o uno nuevo creado en el momento.
 */
function ConnectorPicker({ kind, node, draft, tool, connectors, onChangeDraft, onNewConnector, sample, onSample }) {
  const slots = (draft.connectors || []).filter((s) => s.kind === kind);
  const fixedIds = new Set(slots.filter((s) => s.mode === 'fixed').map((s) => s.connector_id));
  // Una global con conector fijo solo puede usar conectores globales; una personal, los globales y los de su proyecto.
  const usable = connectors.filter((c) => c.kind === kind && (tool.scope === 'global' ? c.project_id === null : (c.project_id === null || c.project_id === tool.project_id)));
  const name = (id) => connectors.find((c) => c.id === id)?.name || `conector ${id}`;
  const options = [
    ...slots.map((s) => ({ value: `slot:${s.slot}`, label: s.mode === 'fixed' ? name(s.connector_id) : (s.label || 'Lo elige cada agente'), hint: s.mode === 'fixed' ? 'ya lo usa esta herramienta' : 'cada agente elige el suyo' })),
    ...usable.filter((c) => !fixedIds.has(c.id)).map((c) => ({ value: `con:${c.id}`, label: c.name, hint: c.project_id === null ? 'global' : 'de este proyecto' })),
    ...(tool.scope === 'global' && !slots.some((s) => s.mode === 'agent') ? [{ value: 'agent:new', label: 'Que lo elija cada agente', hint: 'cada cliente usa el suyo; aquí eliges uno para probar' }] : []),
  ];
  const pick = (v, created) => {
    let connectorsNext = draft.connectors || [];
    let slot;
    if (v.startsWith('slot:')) slot = v.slice(5);
    else {
      slot = nextSlot(connectorsNext);
      const id = v.startsWith('con:') ? Number(v.slice(4)) : null;
      connectorsNext = [...connectorsNext, { slot, kind, mode: id ? 'fixed' : 'agent', connector_id: id, label: id ? (created?.name || name(id)) : `${CONNECTOR_KINDS[kind]} de cada agente` }];
    }
    onChangeDraft({ ...draft, connectors: connectorsNext, nodes: updateNode(draft.nodes || [], node.id, { slot }) });
  };
  const current = slots.find((s) => s.slot === node.slot);
  return (
    <Section title="Conector" hint={options.length ? 'De dónde lee o a dónde escribe este paso.' : `Todavía no hay conectores de ${CONNECTOR_KINDS[kind]}. Créalo aquí mismo.`}>
      <div className="space-y-2">
        {options.length > 0 && <Combo value={node.slot ? `slot:${node.slot}` : ''} onChange={(v) => pick(v)} options={options} ariaLabel="Conector del paso" placeholder="Elige un conector" />}
        <Btn size="sm" variant={options.length ? 'ghost' : 'primary'} icon={FaPlus} onClick={() => onNewConnector(kind, (r) => pick(`con:${r.id}`, r))}>Crear conector de {CONNECTOR_KINDS[kind]}</Btn>
        {current?.mode === 'agent' && (
          <Field label="Para leer tablas y probar aquí, usa" hint="Solo en este editor. En cada agente se elige el suyo.">
            <Combo value={sample[current.slot] ? String(sample[current.slot]) : ''} onChange={(v) => onSample(current.slot, Number(v) || null)}
              options={connectors.filter((c) => c.kind === kind).map((c) => ({ value: String(c.id), label: c.name, hint: c.project_id === null ? 'global' : 'de un proyecto' }))} ariaLabel="Conector de prueba" placeholder="Elige uno de prueba" />
          </Field>
        )}
      </div>
    </Section>
  );
}

// Automática = la base la llena sola: tiene valor por defecto, o es una llave numérica (autoincrementable; un motor anterior no lo marcaba).
const isAuto = (c) => c.hasDefault || (c.pk && /^(bigint|integer|smallint|int2|int4|int8)$/.test(c.type));
const colOption = (c) => ({
  value: c.name,
  hint: [c.type, c.fk ? `→ ${c.fk}` : null, c.enum?.length ? `opciones: ${c.enum.join(', ')}` : null].filter(Boolean).join(' · '),
  tags: [c.pk && 'llave', c.required && !isAuto(c) && 'obligatoria', isAuto(c) && 'automática'].filter(Boolean),
});
const splitCols = (v) => String(v || '').split(',').map((x) => x.trim()).filter(Boolean);

function SupabaseEditor({ node, set, vars, connectorId }) {
  const [reload, setReload] = useState(0);
  const schema = useSchema(connectorId, reload);
  const ready = schema.state === 'ready';
  const table = schema.tables?.find((t) => t.name === node.table);
  const colOptions = (table?.columns || []).map(colOption);
  const rpc = node.operation === 'rpc';
  const fn = schema.functions?.find((f) => f.name === node.fn);
  const used = new Set((node.values || []).map((v) => v.key));
  const missingRequired = !rpc && ['insert', 'upsert'].includes(node.operation) && table ? table.columns.filter((c) => c.required && !isAuto(c) && !used.has(c.name)) : [];
  const fillColumns = () => set({ values: table.columns.filter((c) => !isAuto(c)).map((c) => ({ key: c.name, value: (node.values || []).find((v) => v.key === c.name)?.value || '' })) });
  const pickTable = (t) => set(t === node.table ? {} : { table: t, values: [], filters: [], on_conflict: '', columns: '' });
  const status = !connectorId ? <p className="text-[12.5px] text-fg-dim">Elige arriba el conector para ver sus tablas.</p>
    : schema.state === 'error' ? <p role="alert" className="text-[12.5px] text-signal">{schema.message} <button type="button" className="underline" onClick={() => { schemaCache.delete(connectorId); setReload((n) => n + 1); }}>Reintentar</button></p>
    : null;
  return (
    <>
      <Section title="Qué hace">
        <NativeSelect value={node.operation} onChange={(e) => set({ operation: e.target.value })} aria-label="Operación">
          {SUPABASE_OPS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </NativeSelect>
        <p className="mt-1.5 text-[12px] text-fg-faint">No hay «borrar»: las herramientas de esta versión no borran datos.</p>
      </Section>
      <Section title={rpc ? 'Función' : 'Tabla'} hint={ready ? `${rpc ? schema.functions.length + ' funciones' : schema.tables.length + ' tablas y vistas'} en esta base.` : undefined}>
        {status || (
          <Combo value={rpc ? node.fn || '' : node.table || ''} loading={schema.state === 'loading'} ariaLabel={rpc ? 'Función' : 'Tabla'} placeholder={rpc ? 'Elige la función' : 'Elige la tabla'}
            onChange={(v) => (rpc ? set({ fn: v, values: [] }) : pickTable(v))} emptyText={rpc ? 'Esta base no tiene funciones públicas.' : 'Esta base no tiene tablas públicas.'}
            options={rpc ? (schema.functions || []).map((f) => ({ value: f.name, hint: f.args.length ? `recibe: ${f.args.join(', ')}` : 'no recibe datos' }))
              : (schema.tables || []).map((t) => ({ value: t.name, hint: `${t.columns.length} columnas` }))} />
        )}
      </Section>
      {ready && (rpc ? fn : table) && (
        <>
          {node.operation !== 'select' && (
            <Section title={rpc ? 'Datos que recibe la función' : 'Columnas y valores'} hint={rpc ? undefined : 'Elige la columna y pon su valor (texto o un dato con { }). «Automática» = la base la llena sola.'}
              action={table && !rpc && <Btn size="sm" variant="ghost" onClick={fillColumns}>Poner todas</Btn>}>
              <KVList items={node.values} onChange={(values) => set({ values })} vars={vars} keyLabel={rpc ? 'dato' : 'columna'} addLabel={rpc ? 'Agregar dato' : 'Agregar columna'} idPrefix={node.id}
                keyChoices={rpc ? (fn.args || []).map((x) => ({ value: x })) : colOptions} />
              {missingRequired.length > 0 && <p className="mt-2 text-[12.5px] text-caution">Faltan columnas obligatorias: {missingRequired.map((c) => c.name).join(', ')}. Sin ellas, Supabase rechaza la fila.</p>}
            </Section>
          )}
          {!rpc && (node.operation === 'select' || node.operation === 'update') && (
            <Section title="Filtros" hint={node.operation === 'update' ? 'Solo se actualizan las filas que cumplen todos los filtros.' : 'Solo se devuelven las filas que cumplen todos los filtros.'}>
              <Filters node={node} set={set} vars={vars} colOptions={colOptions} />
            </Section>
          )}
          {!rpc && node.operation === 'upsert' && (
            <Section title="Si ya existe, buscar por" hint="La columna (o columnas) única que identifica la fila. Si ya hay una con ese valor, se actualiza en vez de duplicarse.">
              <Combo multiple value={splitCols(node.on_conflict)} onChange={(arr) => set({ on_conflict: arr.join(',') })} options={colOptions} ariaLabel="Columnas únicas" placeholder="Elige la columna única" />
            </Section>
          )}
          {!rpc && (
            <Section title={node.operation === 'select' ? 'Columnas a devolver' : 'Columnas que devuelve al terminar'} hint="Sin elegir = todas. Menos columnas = menos tokens para el agente.">
              <Combo multiple value={splitCols(node.columns)} onChange={(arr) => set({ columns: arr.join(',') })} options={colOptions} ariaLabel="Columnas a devolver" placeholder="Todas las columnas" />
            </Section>
          )}
          {node.operation === 'select' && (
            <Section title="Máximo de filas"><TextInput inputMode="numeric" value={String(node.limit ?? 20)} onChange={(e) => set({ limit: Number(e.target.value.replace(/\D/g, '')) || 1 })} className="w-28" /></Section>
          )}
        </>
      )}
    </>
  );
}

function Filters({ node, set, vars, colOptions }) {
  const list = node.filters || [];
  const up = (i, patch) => set({ filters: list.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  return (
    <div className="space-y-2">
      {list.map((f, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,2fr)_auto] gap-2 items-start">
          <Combo value={f.column} onChange={(column) => up(i, { column })} options={colOptions} ariaLabel={`Columna del filtro ${i + 1}`} placeholder="Columna" />
          <NativeSelect value={f.op} onChange={(e) => up(i, { op: e.target.value })} aria-label={`Comparación del filtro ${i + 1}`}>{FILTER_OPS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}</NativeSelect>
          <ExprInput value={f.value} onChange={(value) => up(i, { value })} vars={vars} ariaLabel={`Valor del filtro ${i + 1}`} />
          <IconBtn label="Quitar filtro" onClick={() => set({ filters: list.filter((_, j) => j !== i) })}><FaTimes /></IconBtn>
        </div>
      ))}
      <Btn size="sm" variant="ghost" icon={FaPlus} onClick={() => set({ filters: [...list, { column: '', op: 'eq', value: '' }] })}>Agregar filtro</Btn>
    </div>
  );
}

function IfEditor({ node, set, vars }) {
  const list = node.conditions || [];
  const up = (i, patch) => set({ conditions: list.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  return (
    <>
      <Section title="Condiciones" hint="Si se cumplen, sigue por «Sí»; si no, por «No». Compara números como números y texto sin importar mayúsculas.">
        <div className="space-y-3">
          {list.map((c, i) => (
            <div key={i} className="rounded-xl ring-1 ring-line p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[12px] font-semibold text-fg-faint flex-1">{i === 0 ? 'Si' : node.match === 'any' ? 'o si' : 'y si'}</span>
                {list.length > 1 && <IconBtn label={`Quitar la condición ${i + 1}`} onClick={() => set({ conditions: list.filter((_, j) => j !== i) })}><FaTimes /></IconBtn>}
              </div>
              <ExprInput value={c.left} onChange={(left) => up(i, { left })} vars={vars} ariaLabel={`Dato a revisar ${i + 1}`} placeholder="{{entrada.calificacion}}" />
              <NativeSelect value={c.op} onChange={(e) => up(i, { op: e.target.value })} aria-label={`Comparación ${i + 1}`}>{COND_OPS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}</NativeSelect>
              {!['empty', 'not_empty'].includes(c.op) && <ExprInput value={c.right} onChange={(right) => up(i, { right })} vars={vars} ariaLabel={`Valor a comparar ${i + 1}`} placeholder="7" />}
            </div>
          ))}
          <Btn size="sm" variant="ghost" icon={FaPlus} onClick={() => set({ conditions: [...list, { left: '', op: 'eq', right: '' }] })} disabled={list.length >= 10}>Agregar condición</Btn>
        </div>
      </Section>
      {list.length > 1 && (
        <Section title="Con varias condiciones">
          <div role="radiogroup" aria-label="Cuántas deben cumplirse" className="grid grid-cols-2 gap-1 rounded-xl bg-raised p-1">
            {[{ k: 'all', t: 'Todas' }, { k: 'any', t: 'Al menos una' }].map((o) => (
              <button key={o.k} type="button" role="radio" aria-checked={node.match === o.k} onClick={() => set({ match: o.k })}
                className={`h-8 rounded-lg text-[12px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${node.match === o.k ? 'bg-panel text-fg shadow-sm ring-1 ring-line' : 'text-fg-dim hover:text-fg'}`}>{o.t}</button>
            ))}
          </div>
        </Section>
      )}
      <Section title="Después" hint="Agrega los pasos de cada camino con el «+» del lienzo. Si un camino termina con una respuesta al agente, ahí acaba la herramienta; si no, sigue con lo que va después del «Si / no».">
        <p className="text-[12.5px] text-fg-dim">Resultado disponible como <code className="font-mono">{`{{nodos.${node.id}.resultado}}`}</code> (true o false).</p>
      </Section>
    </>
  );
}

const sheetCache = new Map();
const SHEET_ID = /\/spreadsheets\/d\/([A-Za-z0-9_-]+)|^([A-Za-z0-9_-]{20,100})$/;
function SheetsEditor({ node, set, vars, connectorId }) {
  const key = `${connectorId}:${node.spreadsheet}`;
  const [info, setInfo] = useState(() => sheetCache.get(key) || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const looksOk = SHEET_ID.test((node.spreadsheet || '').trim());
  // Se lee sola al pegar el enlace (y al cambiar de conector): pestañas y encabezados salen de la propia hoja.
  useEffect(() => {
    setError('');
    if (!connectorId || !looksOk) { setInfo(null); return undefined; }
    if (sheetCache.get(key)) { setInfo(sheetCache.get(key)); return undefined; }
    let live = true; setBusy(true);
    const t = setTimeout(() => {
      engineService.connectorSheets(connectorId, node.spreadsheet)
        .then((r) => { sheetCache.set(key, r); if (live) setInfo(r); })
        .catch((e) => { if (live) { setInfo(null); setError(e.message); } })
        .finally(() => { if (live) setBusy(false); });
    }, 500);
    return () => { live = false; clearTimeout(t); setBusy(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorId, node.spreadsheet]);
  const tab = info?.sheets?.find((t) => t.name === node.sheet);
  const headerOptions = (tab?.headers || []).map((h) => ({ value: h }));
  const m = node.match || { column: '', value: '' };
  const pickTab = (t) => set(t === node.sheet ? {} : { sheet: t, values: [], match: { column: '', value: m.value } });
  return (
    <>
      <Section title="Qué hace">
        <NativeSelect value={node.operation} onChange={(e) => set({ operation: e.target.value })} aria-label="Operación">{SHEETS_OPS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}</NativeSelect>
        <p className="mt-1.5 text-[12px] text-fg-faint">La fila 1 de la pestaña son los encabezados. No borra filas.</p>
      </Section>
      <Section title="Hoja de cálculo" hint="Pega el enlace de la hoja; se lee sola. Debe estar compartida (como editor) con el correo de la cuenta de servicio del conector.">
        <TextInput value={node.spreadsheet || ''} onChange={(e) => set({ spreadsheet: e.target.value.trim(), sheet: '', values: [] })} placeholder="https://docs.google.com/spreadsheets/d/…" spellCheck={false} aria-label="Enlace de la hoja de cálculo" disabled={!connectorId} />
        {!connectorId && <p className="mt-1.5 text-[12.5px] text-fg-dim">Elige arriba el conector para leer la hoja.</p>}
        {connectorId && node.spreadsheet && !looksOk && <p className="mt-1.5 text-[12.5px] text-caution">Ese no parece un enlace de Google Sheets.</p>}
        {busy && <p className="mt-1.5 text-[12.5px] text-fg-faint" aria-busy="true">Leyendo la hoja…</p>}
        {error && <p role="alert" className="mt-1.5 text-[12.5px] text-signal">{error}</p>}
        {info && <p className="mt-1.5 text-[12px] text-live">«{info.title}» · {info.sheets.length} {info.sheets.length === 1 ? 'pestaña' : 'pestañas'}</p>}
      </Section>
      {info && (
        <>
          <Section title="Pestaña">
            <Combo value={node.sheet || ''} onChange={pickTab} options={info.sheets.map((t) => ({ value: t.name, hint: t.headers.length ? `${t.headers.length} columnas: ${t.headers.slice(0, 4).join(', ')}${t.headers.length > 4 ? '…' : ''}` : 'sin encabezados en la fila 1' }))} ariaLabel="Pestaña" placeholder="Elige la pestaña" />
            {tab && !headerOptions.length && <p className="mt-1.5 text-[12.5px] text-caution">Esa pestaña no tiene encabezados en la fila 1: escríbelos en la hoja y vuelve a pegar el enlace.</p>}
          </Section>
          {tab && headerOptions.length > 0 && (
            <>
              {node.operation !== 'append' && (
                <Section title="Buscar la fila por" hint="La columna y el valor que identifican la fila (sin importar mayúsculas).">
                  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-2">
                    <Combo value={m.column} onChange={(column) => set({ match: { ...m, column } })} options={headerOptions} ariaLabel="Columna para buscar" placeholder="Columna" />
                    <ExprInput value={m.value} onChange={(value) => set({ match: { ...m, value } })} vars={vars} ariaLabel="Valor a buscar" />
                  </div>
                </Section>
              )}
              {node.operation !== 'find' && (
                <Section title={node.operation === 'append' ? 'Columnas de la fila nueva' : 'Columnas que cambian'} hint={node.operation === 'update' ? 'Solo cambian estas; las demás celdas se conservan. Si no encuentra la fila, no cambia nada.' : 'Las columnas que no pongas quedan vacías.'}
                  action={node.operation === 'append' && <Btn size="sm" variant="ghost" onClick={() => set({ values: tab.headers.map((h) => ({ key: h, value: (node.values || []).find((v) => v.key === h)?.value || '' })) })}>Poner todas</Btn>}>
                  <KVList items={node.values} onChange={(values) => set({ values })} vars={vars} keyLabel="columna" keyChoices={headerOptions} addLabel="Agregar columna" idPrefix={`${node.id}-v`} />
                </Section>
              )}
              {node.operation === 'find' && (
                <Section title="Máximo de filas"><TextInput inputMode="numeric" value={String(node.limit ?? 20)} onChange={(e) => set({ limit: Number(e.target.value.replace(/\D/g, '')) || 1 })} className="w-28" /></Section>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

/** Botones de una sola elección (pocas opciones, todas a la vista). */
function Seg({ value, onChange, options, label }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1">
      {options.map((o) => (
        <button key={o.key} type="button" role="radio" aria-checked={value === o.key} onClick={() => onChange(o.key)}
          className={`h-8 px-3 rounded-full text-[12.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${value === o.key ? 'bg-raised text-fg ring-1 ring-line' : 'text-fg-dim hover:text-fg'}`}>{o.label}</button>
      ))}
    </div>
  );
}

const Lbl = ({ label, children }) => <div><p className="text-[13px] font-medium text-fg mb-1.5">{label}</p>{children}</div>;

function HttpEditor({ node, set, vars, picker }) {
  const auth = httpAuthType(node);
  const a = node.auth || { type: auth };
  const hasBody = node.method !== 'GET';
  const mode = node.body_mode || ((node.body || []).length ? 'fields' : 'none');
  const url = node.url ?? node.path ?? '';
  const setAuth = (patch) => set({ auth: { ...a, ...patch }, ...(node.url === undefined && patch.type && patch.type !== 'connector' ? { url: 'https://' } : {}) });
  const setMethod = (method) => set({ method, ...(method === 'GET' ? { body_mode: 'none' } : {}) });
  return (
    <>
      <Section title="Dirección" hint={auth === 'connector' ? 'Empieza con / y se agrega a la dirección base del conector.' : 'La dirección completa del servicio, con https://. Usa { } para meter datos en cualquier parte (los de la ruta y los parámetros se codifican solos).'}>
        <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-2">
          <NativeSelect value={node.method} onChange={(e) => setMethod(e.target.value)} aria-label="Método">{['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m}>{m}</option>)}</NativeSelect>
          <ExprInput value={url} onChange={(v) => set(node.url === undefined && auth === 'connector' ? { path: v } : { url: v })} vars={vars} ariaLabel="Dirección" placeholder={auth === 'connector' ? '/leads/{{entrada.id}}' : 'https://api.ejemplo.com/leads/{{entrada.id}}'} />
        </div>
      </Section>
      <Section title="Identificación" hint="Cómo se presenta la petición ante el servicio.">
        <div className="space-y-3">
          <Seg value={auth} onChange={(type) => setAuth({ type })} options={HTTP_AUTHS} label="Identificación" />
          {auth === 'bearer' && <Lbl label="Token"><ExprInput value={a.token || ''} onChange={(token) => setAuth({ token })} vars={vars} ariaLabel="Token" placeholder="{{entrada.token}}" /></Lbl>}
          {auth === 'basic' && (
            <div className="grid grid-cols-2 gap-2">
              <Lbl label="Usuario"><ExprInput value={a.user || ''} onChange={(user) => setAuth({ user })} vars={vars} ariaLabel="Usuario" /></Lbl>
              <Lbl label="Contraseña"><ExprInput value={a.password || ''} onChange={(password) => setAuth({ password })} vars={vars} ariaLabel="Contraseña" /></Lbl>
            </div>
          )}
          {auth === 'header' && (
            <div className="grid grid-cols-2 gap-2">
              <Lbl label="Nombre del encabezado"><TextInput value={a.name || ''} onChange={(e) => setAuth({ name: e.target.value })} placeholder="X-Api-Key" spellCheck={false} aria-label="Nombre del encabezado" /></Lbl>
              <Lbl label="Clave"><ExprInput value={a.value || ''} onChange={(value) => setAuth({ value })} vars={vars} ariaLabel="Clave" /></Lbl>
            </div>
          )}
          {['bearer', 'basic', 'header'].includes(auth) && <p className="text-[12px] text-caution">Lo que escribas aquí queda guardado en la herramienta. Para una clave real conviene un conector: ahí se guarda cifrada y no se ve.</p>}
          {auth === 'connector' && picker}
        </div>
      </Section>
      <Section title="Parámetros de la dirección (query)" hint="Van después del ? en la dirección, en cualquier método.">
        <KVList items={node.query} onChange={(query) => set({ query })} vars={vars} addLabel="Agregar parámetro" idPrefix={`${node.id}-q`} />
      </Section>
      {hasBody && (
        <Section title="Cuerpo" hint={mode === 'json' ? 'Escribe el JSON. Un dato entre comillas se manda como texto; sin comillas, con su tipo (número, sí/no, lista).' : mode === 'fields' ? 'Cada fila es un campo del JSON que se envía.' : mode === 'text' ? 'Texto tal cual, con datos insertados.' : 'Esta petición no manda cuerpo.'}>
          <div className="space-y-3">
            <Seg value={mode} onChange={(body_mode) => set({ body_mode })} options={HTTP_BODY_MODES} label="Tipo de cuerpo" />
            {mode === 'fields' && <KVList items={node.body} onChange={(body) => set({ body })} vars={vars} keyLabel="campo" addLabel="Agregar campo" idPrefix={`${node.id}-b`} />}
            {(mode === 'json' || mode === 'text') && (
              <ExprInput multiline value={node.body_raw || ''} onChange={(body_raw) => set({ body_raw })} vars={vars} ariaLabel="Cuerpo"
                placeholder={mode === 'json' ? '{\n  "nombre": "{{entrada.nombre}}",\n  "edad": {{entrada.edad}},\n  "extra": {{= ({ total: entrada.edad * 2 }) }}\n}' : 'Hola {{entrada.nombre}}'} />
            )}
          </div>
        </Section>
      )}
      <Section title="Encabezados" hint="Otros encabezados de la petición (por ejemplo X-Origen). El Content-Type del cuerpo se pone solo; si escribes uno aquí, manda el tuyo."><KVList items={node.headers} onChange={(headers) => set({ headers })} vars={vars} keyLabel="encabezado" addLabel="Agregar encabezado" idPrefix={`${node.id}-h`} /></Section>
    </>
  );
}

const HELPERS = 'entrada (datos de la herramienta), nodos (lo que devolvieron los pasos anteriores), fechaMx(), formatoFecha(f, "corta"|"larga"|"hora"), sumarDias(f, n), pesos(n)';

function CodeEditor({ node, set, vars }) {
  const list = node.vars || [];
  const up = (i, patch) => set({ vars: list.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  return (
    <>
      <Section title="Variables" hint="Los datos que entran al código. Cada una se usa por su nombre (por ejemplo precio * cantidad) y llega con el tipo que elijas: número, texto, sí/no, objeto o lista.">
        <div className="space-y-3">
          {list.map((v, i) => (
            <div key={i} className="rounded-xl ring-1 ring-line p-2.5 space-y-2">
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-center">
                <input value={v.key} onChange={(e) => up(i, { key: e.target.value })} aria-label={`Nombre de la variable ${i + 1}`} placeholder="nombre" spellCheck={false} className={`${controlClass} h-10 font-mono text-[12.5px]`} />
                <NativeSelect value={v.type || 'auto'} onChange={(e) => up(i, { type: e.target.value })} aria-label={`Tipo de ${v.key || i + 1}`}>{VAR_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</NativeSelect>
                <button type="button" onClick={() => set({ vars: list.filter((_, j) => j !== i) })} aria-label={`Quitar ${v.key || 'variable'}`} className="h-10 w-9 grid place-items-center rounded-lg text-fg-faint hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60"><FaTimes aria-hidden className="h-3 w-3" /></button>
              </div>
              <ExprInput value={v.value} onChange={(value) => up(i, { value })} vars={vars} ariaLabel={`Valor de ${v.key || i + 1}`} placeholder={v.type === 'json' ? '{{nodos.buscar.data}}' : 'Un dato { } o un valor escrito'} />
            </div>
          ))}
          <button type="button" onClick={() => set({ vars: [...list, { key: '', value: '', type: 'auto' }] })}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12.5px] font-semibold text-fg-dim ring-1 ring-line hover:text-fg hover:ring-fg/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60"><FaPlus aria-hidden className="h-2.5 w-2.5" />Agregar variable</button>
        </div>
      </Section>
      <Section title="Código (JavaScript)" hint={`Lo que devuelvas con return queda en {{nodos.${node.id}}} para los pasos siguientes (un número, texto, lista u objeto). Sin red, archivos ni claves; máximo 8000 caracteres y 0.4 s. Disponible: ${HELPERS}.`}>
        <TextArea value={node.code || ''} onChange={(e) => set({ code: e.target.value })} rows={14} spellCheck={false} aria-label="Código" className="font-mono text-[12.5px] leading-relaxed" placeholder={'const total = precio * cantidad;\nreturn { total, texto: pesos(total) };'} />
      </Section>
    </>
  );
}

function ToolSettings({ draft, tool, onChange, connectors, sample, onSample, warnings }) {
  const set = (patch) => onChange({ ...draft, ...patch });
  return (
    <div className="divide-y divide-line [&>*]:pb-5">
      <div><p className="text-[12px] font-semibold text-fg-faint">Cuando el agente la usa</p></div>
      {warnings?.length > 0 && <ul className="pt-4 text-[12.5px] text-caution list-disc pl-4">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
      <Section title="Para qué sirve" hint="El modelo lee esta descripción para decidir cuándo usarla. Di qué hace y en qué situación.">
        <TextArea rows={3} value={draft.description} onChange={(e) => set({ description: e.target.value })} maxLength={1000} placeholder="Registra a la persona como lead en la base del cliente, con un resumen y una calificación." aria-label="Para qué sirve" />
      </Section>
      <Section title="Cómo usarla" hint="Se agrega sola a las instrucciones del agente, junto con el nombre. Reglas: cuándo sí, cuándo no, cuántas veces.">
        <TextArea rows={3} value={draft.instructions} onChange={(e) => set({ instructions: e.target.value })} maxLength={2000} placeholder="Úsala cuando ya tengas el nombre y el interés. Una sola vez por conversación. No la uses si la persona solo pregunta precios." aria-label="Cómo usarla" />
      </Section>
      <Section title="Datos de entrada" hint="Lo que necesita la herramienta. En los pasos se usan como {{entrada.nombre}}."
        action={<Btn size="sm" variant="ghost" icon={FaPlus} onClick={() => set({ inputs: [...(draft.inputs || []), { key: '', source: 'ai', type: 'string', description: '', required: true }] })}>Agregar dato</Btn>}>
        <InputsEditor inputs={draft.inputs || []} onChange={(inputs) => set({ inputs })} />
      </Section>
      <Section title="Conectores" hint={tool.scope === 'global' ? 'Fijo: todos los agentes escriben en el mismo lugar. Lo elige cada agente: cada cliente usa el suyo.' : 'A qué sistema se conectan sus pasos.'}
        action={<Btn size="sm" variant="ghost" icon={FaPlus} onClick={() => set({ connectors: [...(draft.connectors || []), { slot: nextSlot(draft.connectors), kind: 'supabase', mode: tool.scope === 'global' ? 'agent' : 'fixed', connector_id: null, label: '' }] })}>Agregar conector</Btn>}>
        <SlotsEditor slots={draft.connectors || []} tool={tool} connectors={connectors} onChange={(c) => set({ connectors: c })} sample={sample} onSample={onSample} />
      </Section>
    </div>
  );
}
const nextSlot = (list = []) => { const used = new Set(list.map((s) => s.slot)); let i = list.length + 1; while (used.has(`conector_${i}`)) i++; return list.length ? `conector_${i}` : 'principal'; };

function InputsEditor({ inputs, onChange }) {
  const up = (i, patch) => onChange(inputs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  if (!inputs.length) return <p className="text-[12.5px] text-fg-faint">Ninguno todavía. Ejemplo: <code className="font-mono">resumen</code>, que saca la IA con la descripción «Resumen del lead en 2 líneas».</p>;
  return (
    <ul className="space-y-3">
      {inputs.map((d, i) => (
        <li key={i} className="rounded-xl ring-1 ring-line p-3 space-y-2.5">
          <div className="flex gap-2">
            <input value={d.key} onChange={(e) => up(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="nombre_del_dato" aria-label={`Nombre del dato ${i + 1}`} spellCheck={false} className={`${controlClass} h-9 font-mono text-[12.5px] flex-1`} />
            <IconBtn label={`Quitar ${d.key || 'dato'}`} onClick={() => onChange(inputs.filter((_, j) => j !== i))}><FaTimes /></IconBtn>
          </div>
          <div role="radiogroup" aria-label={`Origen de ${d.key || 'el dato'}`} className="grid grid-cols-3 gap-1 rounded-xl bg-raised p-1">
            {INPUT_SOURCES.map((s) => (
              <button key={s.key} type="button" role="radio" aria-checked={d.source === s.key} title={s.hint} onClick={() => up(i, { source: s.key })}
                className={`h-8 rounded-lg text-[12px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${d.source === s.key ? 'bg-panel text-fg shadow-sm ring-1 ring-line' : 'text-fg-dim hover:text-fg'}`}>{s.label}</button>
            ))}
          </div>
          {d.source === 'ai' && (
            <>
              <TextArea rows={2} value={d.description || ''} onChange={(e) => up(i, { description: e.target.value })} placeholder="Qué va aquí y cómo sacarlo de la conversación" aria-label={`Descripción de ${d.key}`} />
              <div className="flex flex-wrap items-center gap-3">
                <NativeSelect value={d.type} onChange={(e) => up(i, { type: e.target.value })} aria-label={`Tipo de ${d.key}`} className="!w-auto">{INPUT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</NativeSelect>
                <label className="inline-flex items-center gap-2 text-[12.5px] text-fg-dim"><input type="checkbox" checked={d.required !== false} onChange={(e) => up(i, { required: e.target.checked })} className="h-4 w-4 accent-[rgb(var(--live))]" />Obligatorio</label>
              </div>
              {d.type === 'enum' && <TextInput value={(d.options || []).join(', ')} onChange={(e) => up(i, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="alto, medio, bajo" aria-label={`Opciones de ${d.key}`} />}
            </>
          )}
          {d.source === 'fixed' && <TextInput value={d.value ?? ''} onChange={(e) => up(i, { value: e.target.value })} placeholder="Valor" aria-label={`Valor fijo de ${d.key}`} />}
          {d.source === 'context' && (
            <NativeSelect value={d.context || ''} onChange={(e) => up(i, { context: e.target.value })} aria-label={`Dato de la conversación para ${d.key}`}>
              <option value="">Elige qué dato</option>{CONTEXT_KEYS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </NativeSelect>
          )}
        </li>
      ))}
    </ul>
  );
}

function SlotsEditor({ slots, tool, connectors, onChange, sample, onSample }) {
  const up = (i, patch) => onChange(slots.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  // Una global con conector fijo solo puede usar conectores globales (los de un proyecto no se leen desde otro).
  const usable = (kind, mode) => connectors.filter((c) => c.kind === kind && (tool.scope === 'global' && mode === 'fixed' ? c.project_id === null : tool.scope === 'global' || c.project_id === null || c.project_id === tool.project_id));
  if (!slots.length) return <p className="text-[12.5px] text-fg-faint">Ninguno. Los pasos de Supabase y de API necesitan uno. Los conectores se dan de alta en Integraciones.</p>;
  return (
    <ul className="space-y-3">
      {slots.map((s, i) => (
        <li key={s.slot} className="rounded-xl ring-1 ring-line p-3 space-y-2.5">
          <div className="flex gap-2">
            <TextInput value={s.label || ''} onChange={(e) => up(i, { label: e.target.value })} placeholder={`Nombre (p. ej. Supabase del cliente)`} aria-label={`Nombre del conector ${s.slot}`} className="!h-9 flex-1" />
            <NativeSelect value={s.kind} onChange={(e) => up(i, { kind: e.target.value, connector_id: null })} aria-label="Tipo" className="!w-auto !h-9">{Object.entries(CONNECTOR_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</NativeSelect>
            <IconBtn label="Quitar conector" onClick={() => onChange(slots.filter((_, j) => j !== i))}><FaTimes /></IconBtn>
          </div>
          {tool.scope === 'global' && (
            <div role="radiogroup" aria-label="Quién elige el conector" className="grid grid-cols-2 gap-1 rounded-xl bg-raised p-1">
              {[{ k: 'agent', t: 'Lo elige cada agente' }, { k: 'fixed', t: 'Fijo para todos' }].map((o) => (
                <button key={o.k} type="button" role="radio" aria-checked={s.mode === o.k} onClick={() => up(i, { mode: o.k, connector_id: null })}
                  className={`h-8 rounded-lg text-[12px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${s.mode === o.k ? 'bg-panel text-fg shadow-sm ring-1 ring-line' : 'text-fg-dim hover:text-fg'}`}>{o.t}</button>
              ))}
            </div>
          )}
          {s.mode === 'fixed' ? (
            <NativeSelect value={s.connector_id || ''} onChange={(e) => up(i, { connector_id: Number(e.target.value) || null })} aria-label="Conector">
              <option value="">Elige el conector</option>{usable(s.kind, 'fixed').map((c) => <option key={c.id} value={c.id}>{c.name}{c.project_id === null ? ' (global)' : ''}</option>)}
            </NativeSelect>
          ) : (
            <Field label="Para leer tablas y probar aquí, usa" hint="Solo en este editor. En cada agente se elige el suyo.">
              <NativeSelect value={sample[s.slot] || ''} onChange={(e) => onSample(s.slot, Number(e.target.value) || null)}>
                <option value="">Ninguno</option>{usable(s.kind, 'agent').map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </NativeSelect>
            </Field>
          )}
          <p className="text-[11.5px] text-fg-faint font-mono">ranura: {s.slot}</p>
        </li>
      ))}
    </ul>
  );
}
